import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../../lib/apiError';

export const TEACHER_TICKET_CATEGORIES = [
  ['payment_issue', 'Payment / payout'],
  ['technical_issue', 'Technical issue'],
  ['student_complaint', 'Issue with a student'],
  ['academic_issue', 'Academic / content'],
  ['general_query', 'General query'],
];

const EMPTY = { title: '', category: 'general_query', priority: 'medium', description: '' };

const TeacherTicketDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tickets`, form);
      toast.success('Ticket raised. The admin team has been notified.');
      setForm(EMPTY);
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(apiError(e, 'Could not raise ticket'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid="teacher-raise-ticket-button">
        <Plus className="w-4 h-4 mr-2" />Raise a Ticket
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="teacher-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Raise a ticket</DialogTitle>
            <DialogDescription>Tell the admin team about any issue. You'll be notified when they reply.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => set('title')(e.target.value)} data-testid="teacher-ticket-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={set('category')}>
                  <SelectTrigger data-testid="teacher-ticket-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TEACHER_TICKET_CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={set('priority')}>
                  <SelectTrigger data-testid="teacher-ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => set('description')(e.target.value)} data-testid="teacher-ticket-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.title.trim() || !form.description.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid="teacher-ticket-submit-button">
              {saving ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeacherTicketDialog;
